/**
 * Talk Down Messages store. Loads from the admin CRUD API (ENG-2179).
 * Starts empty; shows an empty table when the user has no messages.
 */
import { create } from "zustand";
import {
  blobUrlToFile,
  createTalkDownMessage,
  deleteTalkDownMessage,
  isTalkDownMessagesApiUnavailable,
  listTalkDownMessages,
  patchTalkDownMessage,
  putTalkDownAssignments,
} from "./talkDownMessagesApi";
import {
  CreateTalkDownMessageInput,
  TalkDownAssignment,
  TalkDownMessage,
} from "./talkDownMessageTypes";

interface TalkDownMessagesState {
  messages: TalkDownMessage[];
  /** When true, mutations hit the admin API. */
  apiLive: boolean;
  loading: boolean;
  loadError: string | null;
  reset: () => void;
  loadMessages: (groupId?: string | number) => Promise<void>;
  addMessage: (input: CreateTalkDownMessageInput) => Promise<TalkDownMessage>;
  updateMessage: (
    id: string,
    patch: Partial<
      Pick<
        TalkDownMessage,
        | "name"
        | "audioUrl"
        | "durationSeconds"
        | "assignments"
        | "playCount"
        | "recordedAt"
        | "recordedBy"
      >
    >
  ) => Promise<void>;
  setAssignments: (
    id: string,
    assignments: TalkDownAssignment[]
  ) => Promise<void>;
  removeAssignment: (id: string, siteId: string | number) => Promise<void>;
  deleteMessage: (id: string) => Promise<void>;
  getById: (id: string) => TalkDownMessage | undefined;
}

let nextId = 100;

const localAdd = (
  input: CreateTalkDownMessageInput
): TalkDownMessage => {
  nextId += 1;
  return {
    id: `msg-${nextId}`,
    name: input.name,
    groupId: input.groupId,
    groupName: input.groupName,
    durationSeconds: input.durationSeconds,
    recordedBy: input.recordedBy,
    recordedAt: new Date().toISOString(),
    playCount: 0,
    audioUrl: input.audioUrl,
    assignments: input.assignments.map((a) => ({
      ...a,
      cameraIds: [...a.cameraIds],
      cameraNames: [...a.cameraNames],
    })),
  };
};

export const useTalkDownMessagesStore = create<TalkDownMessagesState>(
  (set, get) => ({
    messages: [],
    apiLive: false,
    loading: true,
    loadError: null,

    reset: () => {
      nextId = 100;
      set({
        messages: [],
        apiLive: false,
        loading: true,
        loadError: null,
      });
    },

    loadMessages: async (groupId) => {
      set({ loading: true, loadError: null });
      try {
        const messages = await listTalkDownMessages(groupId);
        set({ messages, apiLive: true, loading: false, loadError: null });
      } catch (err) {
        if (isTalkDownMessagesApiUnavailable(err)) {
          set({
            messages: [],
            apiLive: false,
            loading: false,
            loadError: null,
          });
          return;
        }
        set({
          loading: false,
          loadError: "Failed to load talk-down messages.",
        });
      }
    },

    addMessage: async (input) => {
      // Always try the admin API first (Alerts wizard never visits /talkdown,
      // so apiLive may still be false). Fall back to local only if undeployed.
      if (input.audioUrl) {
        try {
          const file = await blobUrlToFile(
            input.audioUrl,
            `${input.name.replace(/\s+/g, "_").toLowerCase()}.webm`
          );
          const created = await createTalkDownMessage({
            name: input.name,
            groupId: input.groupId,
            audio: file,
            durationSeconds: input.durationSeconds,
            assignments: input.assignments,
          });
          set((state) => ({
            messages: [created, ...state.messages],
            apiLive: true,
          }));
          return created;
        } catch (err) {
          if (!isTalkDownMessagesApiUnavailable(err)) throw err;
        }
      }
      const message = localAdd(input);
      set((state) => ({ messages: [message, ...state.messages] }));
      return message;
    },

    updateMessage: async (id, patch) => {
      if (patch.name != null || patch.audioUrl) {
        try {
          let audio: File | undefined;
          if (patch.audioUrl?.startsWith("blob:")) {
            audio = await blobUrlToFile(patch.audioUrl, "message.webm");
          }
          const updated = await patchTalkDownMessage(id, {
            name: patch.name,
            audio,
            durationSeconds: patch.durationSeconds,
          });
          set((state) => ({
            apiLive: true,
            messages: state.messages.map((m) =>
              m.id === id ? { ...updated, ...patch, ...updated } : m
            ),
          }));
          // Merge local-only fields (e.g. playCount bumps) after server patch.
          if (patch.playCount != null) {
            set((state) => ({
              messages: state.messages.map((m) =>
                m.id === id ? { ...m, playCount: patch.playCount! } : m
              ),
            }));
          }
          return;
        } catch (err) {
          if (!isTalkDownMessagesApiUnavailable(err)) throw err;
        }
      }
      set((state) => ({
        messages: state.messages.map((m) =>
          m.id === id ? { ...m, ...patch } : m
        ),
      }));
    },

    setAssignments: async (id, assignments) => {
      try {
        const updated = await putTalkDownAssignments(id, assignments);
        set((state) => ({
          apiLive: true,
          messages: state.messages.map((m) => (m.id === id ? updated : m)),
        }));
        return;
      } catch (err) {
        if (!isTalkDownMessagesApiUnavailable(err)) throw err;
      }
      set((state) => ({
        messages: state.messages.map((m) =>
          m.id === id
            ? {
                ...m,
                assignments: assignments.map((a) => ({
                  ...a,
                  cameraIds: [...a.cameraIds],
                  cameraNames: [...a.cameraNames],
                })),
              }
            : m
        ),
      }));
    },

    removeAssignment: async (id, siteId) => {
      const current = get().messages.find((m) => m.id === id);
      if (!current) return;
      const next = current.assignments.filter(
        (a) => String(a.siteId) !== String(siteId)
      );
      await get().setAssignments(id, next);
    },

    deleteMessage: async (id) => {
      try {
        await deleteTalkDownMessage(id);
        set({ apiLive: true });
      } catch (err) {
        if (!isTalkDownMessagesApiUnavailable(err)) throw err;
      }
      set((state) => ({
        messages: state.messages.filter((m) => m.id !== id),
      }));
    },

    getById: (id) => get().messages.find((m) => m.id === id),
  })
);
